import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

/**
 * TrendIndicator - Display trend direction with icon and color
 * @param {string} trend - "up", "down", "stable", or "n/a"
 * @param {number|null} percentage - Change percentage (positive or negative) or null for n/a
 * @param {string} type - "best" or "slow" (affects color interpretation)
 */
const TrendIndicator = ({ trend, percentage, type = 'best' }) => {
    // Handle N/A case (no historical data for comparison)
    if (trend === 'n/a' || percentage === null) {
        return (
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip>
                        No historical data available for comparison
                    </Tooltip>
                }
            >
                <span
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#6c757d', // Gray
                        fontSize: '14px',
                        fontWeight: '500',
                    }}
                >
                    <i className="material-icons" style={{ fontSize: '18px' }}>
                        help_outline
                    </i>
                    <span>N/A</span>
                </span>
            </OverlayTrigger>
        );
    }

    const getTrendConfig = () => {
        if (type === 'best') {
            // For best sellers: up is good, down is bad
            switch (trend) {
                case 'up':
                    return {
                        icon: 'trending_up',
                        color: '#28a745', // Green
                        label: 'Increasing',
                    };
                case 'down':
                    return {
                        icon: 'trending_down',
                        color: '#dc3545', // Red
                        label: 'Decreasing',
                    };
                case 'stable':
                default:
                    return {
                        icon: 'trending_flat',
                        color: '#ffc107', // Yellow
                        label: 'Stable',
                    };
            }
        } else {
            // For slow sellers: down is concerning, up might mean improving
            switch (trend) {
                case 'down':
                    return {
                        icon: 'trending_down',
                        color: '#dc3545', // Red (getting worse)
                        label: 'Declining further',
                    };
                case 'up':
                    return {
                        icon: 'trending_up',
                        color: '#28a745', // Green (improving)
                        label: 'Improving',
                    };
                case 'stable':
                default:
                    return {
                        icon: 'trending_flat',
                        color: '#ffc107', // Yellow
                        label: 'Consistently slow',
                    };
            }
        }
    };

    const config = getTrendConfig();
    const formattedPercentage = percentage > 0 ? `+${percentage.toFixed(1)}%` : `${percentage.toFixed(1)}%`;

    return (
        <OverlayTrigger
            placement="top"
            overlay={
                <Tooltip>
                    {config.label}: {formattedPercentage} vs previous period
                </Tooltip>
            }
        >
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: config.color,
                    fontSize: '14px',
                    fontWeight: '500',
                }}
            >
                <i className="material-icons" style={{ fontSize: '18px' }}>
                    {config.icon}
                </i>
                <span>{formattedPercentage}</span>
            </span>
        </OverlayTrigger>
    );
};

export default TrendIndicator;
